import { useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import SeatUsageNudge from "../components/SeatUsageNudge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Envelope, Phone, ArrowClockwise, UsersThree, Copy, WarningCircle, CheckCircle, Key, PaperPlaneTilt, Eye, SealWarning, Clock, Trash, Receipt, CurrencyGbp, House } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import AddressAutocomplete from "../components/AddressAutocomplete";

function EmailStatusPill({ status, event, error }) {
  // Map to pill config. Priority: webhook event > initial status
  const map = {
    bounced:    { bg: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200",   icon: SealWarning, label: "Bounced" },
    complained: { bg: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200",   icon: SealWarning, label: "Marked as spam" },
    failed:     { bg: "bg-red-100 text-red-800",   icon: WarningCircle, label: "Failed" },
    delayed:    { bg: "bg-amber-100 text-amber-800", icon: Clock, label: "Delayed" },
    delivered:  { bg: "bg-emerald-100 text-emerald-800", icon: CheckCircle, label: "Delivered" },
    opened:     { bg: "bg-blue-100 text-blue-800", icon: Eye, label: "Opened" },
    sent:       { bg: "bg-slate-100 text-slate-700", icon: PaperPlaneTilt, label: "Sent" },
  };
  const config = map[status];
  if (!config) return <span className="text-[10px] text-slate-400">—</span>;
  const Icon = config.icon;
  const tip = error || (event ? `Last Resend event: ${event}` : "");
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${config.bg}`}
          title={tip}
          data-testid={`in-house-welcome-pill-${status}`}>
      <Icon className="h-3 w-3" /> {config.label}
    </span>
  );
}

function CredentialsDialog({ open, onOpenChange, data }) {
  if (!data) return null;
  const { email, temp_password, email_delivered, email_error } = data;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});
  };
  const copyAll = () => {
    const block = `Email: ${email}\nTemporary password: ${temp_password}\nSign in: ${window.location.origin}/login`;
    copy(block, "Login details");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="in-house-credentials-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-amber-600" /> Login details for your operative
          </DialogTitle>
          <DialogDescription>
            {email_delivered
              ? "Welcome email sent successfully. Copy these details as a backup in case it goes to their spam folder."
              : "Welcome email could not be delivered. Please share these credentials manually via a different channel (SMS, WhatsApp, in person)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {email_delivered ? (
            <div className="flex items-start gap-2 rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <div className="text-xs text-green-900 dark:text-green-200">
                <p>Email accepted for delivery to <strong>{email}</strong>.</p>
                <p className="mt-1 opacity-80">Heads up: email acceptance doesn't guarantee inbox placement — if the recipient can't find the email in 2 minutes, share these credentials via SMS or WhatsApp using the Copy button below.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <WarningCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 dark:text-amber-200">
                <p><strong>Delivery failed.</strong> {email_error || "The recipient's mail server rejected the message."}</p>
                <p className="mt-1">Copy the temporary password below and share it manually.</p>
              </div>
            </div>
          )}

          <div className="rounded-md border border-border p-3 space-y-2 bg-muted/30">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</p>
                <p className="text-sm font-mono truncate">{email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => copy(email, "Email")} data-testid="copy-email-btn">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Temporary password</p>
                <p className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100 select-all" data-testid="credentials-temp-password">
                  {temp_password}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => copy(temp_password, "Password")} data-testid="copy-password-btn">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground pt-1">They'll be prompted to change this on first sign-in.</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copyAll} data-testid="copy-all-credentials-btn">
            <Copy className="h-3.5 w-3.5 mr-2" /> Copy all details
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="credentials-close-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InHouseTeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [testEmailDlg, setTestEmailDlg] = useState({ open: false, email: "", result: null, sending: false });

  // Rate profile editor
  const [rateDlg, setRateDlg] = useState({ open: false, user: null, saving: false, form: {} });

  // Home location editor (used by the operative Route Planner)
  const [homeDlg, setHomeDlg] = useState({ open: false, user: null, saving: false, address: "", latitude: null, longitude: null });
  const openHomeDialog = (u) => {
    const h = u.home_location || {};
    setHomeDlg({ open: true, user: u, saving: false, address: h.address || "", latitude: h.latitude ?? null, longitude: h.longitude ?? null });
  };
  const saveHomeLocation = async () => {
    if (!homeDlg.user) return;
    if (!homeDlg.address.trim()) { toast.error("Enter a home address"); return; }
    setHomeDlg(s => ({ ...s, saving: true }));
    try {
      await API.put(`/in-house/team/${homeDlg.user._id}/home-location`, {
        address: homeDlg.address.trim(),
        latitude: homeDlg.latitude,
        longitude: homeDlg.longitude,
      });
      toast.success(`Home location saved for ${homeDlg.user.name}`);
      setHomeDlg({ open: false, user: null, saving: false, address: "", latitude: null, longitude: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save home location");
      setHomeDlg(s => ({ ...s, saving: false }));
    }
  };

  const openRateDialog = (u) => {
    const rp = u.rate_profile || {};
    setRateDlg({
      open: true, user: u, saving: false,
      form: {
        day_rate: rp.day_rate ?? 180,
        helper_rate: rp.helper_rate ?? 0,
        day_rate_label: rp.day_rate_label ?? "Day rate",
        helper_rate_label: rp.helper_rate_label ?? "Overtime rate",
        helper_rate_enabled: !!rp.helper_rate_enabled,
      },
    });
  };
  const saveRateProfile = async () => {
    if (!rateDlg.user) return;
    setRateDlg(s => ({ ...s, saving: true }));
    try {
      await API.put(`/in-house/team/${rateDlg.user._id}/rate-profile`, rateDlg.form);
      toast.success(`Rates saved for ${rateDlg.user.name}`);
      setRateDlg({ open: false, user: null, saving: false, form: {} });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save rates"); }
    finally { setRateDlg(s => ({ ...s, saving: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/in-house/team");
      setTeam(data.team || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!dialog.name.trim() || !dialog.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.post("/in-house/team", {
        name: dialog.name.trim(),
        email: dialog.email.trim().toLowerCase(),
        phone: dialog.phone.trim(),
      });
      if (data.email_delivered) {
        toast.success("Operative added — welcome email delivered");
      } else {
        toast.warning("Operative created but email delivery failed — share credentials manually");
      }
      setDialog({ open: false, name: "", email: "", phone: "" });
      setCredentials(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create operative");
    } finally {
      setSaving(false);
    }
  };

  const resend = async (userId, email, options = {}) => {
    if (!options.skipConfirm && !window.confirm(`Reset password and resend welcome email to ${email}?`)) return;
    try {
      const { data } = await API.post(`/in-house/team/${userId}/resend-welcome`);
      if (data.email_delivered) {
        toast.success(`New welcome email sent to ${email}`);
      } else {
        toast.warning(`Email delivery failed for ${email} — copy credentials below`);
      }
      setCredentials(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resend");
    }
  };

  const reinvite = (u) => resend(u._id, u.email, { skipConfirm: true });

  const deleteOp = async (u) => {
    const msg = `Permanently delete ${u.name || u.email}?\n\nThis will:\n• Remove them from the database\n• Unassign them from any jobs they're on\n• Delete their notifications & expense records\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await API.delete(`/in-house/team/${u._id}`);
      toast.success(`${u.name || u.email} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete user");
    }
  };

  const showSavedCredentials = async (u) => {
    // No password available post-creation (security: we never store it).
    // Offer to reset & reveal.
    if (!window.confirm(`No saved password. Reset ${u.email}'s password now and reveal the new one?`)) return;
    resend(u._id, u.email);
  };

  return (
    <div className="p-6 space-y-6" data-testid="in-house-team-page">
      <SeatUsageNudge />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <UsersThree className="h-7 w-7 text-amber-600" /> In-House Team
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Your field operatives. They see assigned jobs, capture on-site time, and submit expenses through their own portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/in-house/pos">
            <Button variant="outline" size="sm" data-testid="in-house-pos-link-btn">
              <Receipt className="h-4 w-4 mr-2" /> Purchase Orders
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={() => setTestEmailDlg({ open: true, email: "", result: null, sending: false })}
                  data-testid="in-house-team-test-email-btn">
            Test email delivery
          </Button>
          <Button onClick={() => setDialog({ open: true, name: "", email: "", phone: "" })}
                  data-testid="in-house-team-add-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add operative
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading team…</div>
      ) : team.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-16 text-center">
          <UsersThree className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-4 font-semibold text-slate-700 dark:text-slate-200">No in-house operatives yet</p>
          <p className="text-sm text-slate-500 mt-1">Add your first team member to start assigning them work.</p>
          <Button onClick={() => setDialog({ open: true, name: "", email: "", phone: "" })}
                  className="mt-4" data-testid="in-house-team-empty-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add your first operative
          </Button>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-left font-semibold hidden sm:table-cell">Email</th>
                <th className="px-4 py-3 text-left font-semibold hidden md:table-cell">Phone</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold hidden lg:table-cell">Welcome email</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {team.map((u) => (
                <tr key={u._id} className="border-t border-border" data-testid={`in-house-team-row-${u._id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{u.name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-700 dark:text-slate-300">
                    <span className="inline-flex items-center gap-1"><Envelope className="h-3.5 w-3.5" /> {u.email}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-700 dark:text-slate-300">
                    {u.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {u.phone}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.force_password_change ? (
                      <Badge variant="secondary" className="text-[10px]">Awaiting first login</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 text-[10px]">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <EmailStatusPill status={u.welcome_email_status} event={u.welcome_email_event} error={u.welcome_email_error} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {["bounced", "complained", "failed", "delayed"].includes(u.welcome_email_status) && (
                      <button onClick={() => reinvite(u)}
                              title="One-click re-invite: picks a new memorable password and resends the welcome email"
                              className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white mr-2 shadow-sm"
                              data-testid={`in-house-team-reinvite-${u._id}`}>
                        <ArrowClockwise className="h-3 w-3" weight="bold" /> Re-invite
                      </button>
                    )}
                    <button onClick={() => openRateDialog(u)}
                            title="Set Rate 1 / Rate 2 defaults used to pre-fill PO lines"
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1 mr-3"
                            data-testid={`in-house-team-rates-${u._id}`}>
                      <CurrencyGbp className="h-3.5 w-3.5" />
                      {u.rate_profile?.day_rate > 0 ? `Rates · £${u.rate_profile.day_rate}` : "Rates"}
                    </button>
                    <button onClick={() => openHomeDialog(u)}
                            title="Set home location — used by the operative's day Route Planner"
                            className={`text-xs font-medium inline-flex items-center gap-1 mr-3 ${u.home_location?.latitude ? "text-emerald-600 hover:text-emerald-800" : "text-slate-500 hover:text-slate-900"}`}
                            data-testid={`in-house-team-home-${u._id}`}>
                      <House className="h-3.5 w-3.5" /> Home
                    </button>
                    <button onClick={() => showSavedCredentials(u)}
                            title="Reveal credentials (will reset password)"
                            className="text-slate-500 hover:text-slate-900 text-xs font-medium inline-flex items-center gap-1 mr-3"
                            data-testid={`in-house-team-reveal-${u._id}`}>
                      <Key className="h-3.5 w-3.5" /> Credentials
                    </button>
                    <button onClick={() => resend(u._id, u.email)}
                            title="Reset password & resend welcome"
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1"
                            data-testid={`in-house-team-resend-${u._id}`}>
                      <ArrowClockwise className="h-3.5 w-3.5" /> Resend welcome
                    </button>
                    <button onClick={() => deleteOp(u)}
                            title="Permanently delete this operative"
                            className="text-rose-600 hover:text-rose-800 text-xs font-medium inline-flex items-center gap-1 ml-3"
                            data-testid={`in-house-team-delete-${u._id}`}>
                      <Trash className="h-3.5 w-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent data-testid="in-house-team-dialog">
          <DialogHeader><DialogTitle>Add in-house operative</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full name</Label>
              <Input value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
                     autoFocus placeholder="Jake Harper" data-testid="in-house-team-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={dialog.email}
                     onChange={(e) => setDialog({ ...dialog, email: e.target.value })}
                     placeholder="jake@fixlr.io" data-testid="in-house-team-email" />
              <p className="text-[11px] text-slate-500 mt-1">They'll get a welcome email with login details and be asked to set a password. If delivery fails you'll see the credentials on-screen so you can share them manually.</p>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input value={dialog.phone} onChange={(e) => setDialog({ ...dialog, phone: e.target.value })}
                     placeholder="07700 900123" data-testid="in-house-team-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ ...dialog, open: false })}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="in-house-team-submit">
              {saving ? "Creating…" : "Create & send welcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CredentialsDialog open={!!credentials} onOpenChange={(o) => !o && setCredentials(null)} data={credentials} />

      {/* SMTP Test dialog */}
      <Dialog open={testEmailDlg.open} onOpenChange={(o) => setTestEmailDlg((s) => ({ ...s, open: o, result: o ? s.result : null }))}>
        <DialogContent data-testid="in-house-team-test-email-dialog">
          <DialogHeader><DialogTitle>Test welcome email delivery</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Recipient email</Label>
              <Input type="email" placeholder="hello@example.com"
                     value={testEmailDlg.email}
                     onChange={(e) => setTestEmailDlg((s) => ({ ...s, email: e.target.value }))}
                     autoFocus
                     data-testid="in-house-team-test-email-input" />
              <p className="text-[11px] text-slate-500 mt-1">Sends the real welcome email template (with a dummy password) to this address so you can confirm it lands in the inbox.</p>
            </div>

            {testEmailDlg.result && (
              <div className={`rounded-md border p-3 text-xs space-y-1 ${testEmailDlg.result.delivered ? "border-green-300 bg-green-50 dark:bg-green-950/30" : "border-amber-300 bg-amber-50 dark:bg-amber-950/30"}`}>
                <p className="font-semibold">
                  {testEmailDlg.result.delivered
                    ? `${testEmailDlg.result.provider || "Email"} accepted delivery to ${testEmailDlg.result.to}`
                    : `Email delivery failed`}
                </p>
                {testEmailDlg.result.error && (
                  <p className="text-rose-700 dark:text-rose-300"><strong>Error:</strong> {testEmailDlg.result.error}</p>
                )}
                <p className="text-[11px] text-slate-600 dark:text-slate-400">{testEmailDlg.result.tip}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDlg({ open: false, email: "", result: null, sending: false })}>Close</Button>
            <Button disabled={testEmailDlg.sending || !testEmailDlg.email.trim()}
                    onClick={async () => {
                      setTestEmailDlg((s) => ({ ...s, sending: true, result: null }));
                      try {
                        const { data } = await API.post("/in-house/team/test-email", {
                          email: testEmailDlg.email.trim(),
                        });
                        setTestEmailDlg((s) => ({ ...s, sending: false, result: data }));
                      } catch (e) {
                        setTestEmailDlg((s) => ({
                          ...s, sending: false,
                          result: { delivered: false, error: formatApiError(e.response?.data?.detail) || "Request failed", tip: "" },
                        }));
                      }
                    }}
                    data-testid="in-house-team-test-email-send">
              {testEmailDlg.sending ? "Sending…" : "Send test email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate profile editor */}
      <Dialog open={rateDlg.open} onOpenChange={(o) => { if (!o) setRateDlg({ open: false, user: null, saving: false, form: {} }); }}>
        <DialogContent className="max-w-md" data-testid="in-house-team-rates-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Pay Rates — {rateDlg.user?.name}</DialogTitle>
            <DialogDescription>
              These default rates pre-fill new POs for this operative. You can still override them per PO.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Day rate name</Label>
                <Input value={rateDlg.form.day_rate_label || ""} onChange={(e) => setRateDlg(s => ({ ...s, form: { ...s.form, day_rate_label: e.target.value } }))} placeholder="Day rate" data-testid="rates-dr1-label" />
              </div>
              <div>
                <Label className="text-xs">Day rate default (£)</Label>
                <Input type="number" step="0.01" value={rateDlg.form.day_rate ?? 0} onChange={(e) => setRateDlg(s => ({ ...s, form: { ...s.form, day_rate: parseFloat(e.target.value) || 0 } }))} data-testid="rates-day-rate" />
              </div>
            </div>
            <div className="rounded-md border border-border p-3 space-y-2" data-testid="rates-second-rate-block">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-xs">Second rate column</Label>
                  <p className="text-[11px] text-muted-foreground">Off by default. Turn on for an extra per-line rate, e.g. an overtime or weekend rate.</p>
                </div>
                <Switch checked={!!rateDlg.form.helper_rate_enabled} onCheckedChange={(v) => setRateDlg(s => ({ ...s, form: { ...s.form, helper_rate_enabled: v } }))} data-testid="rates-second-rate-toggle" />
              </div>
              {rateDlg.form.helper_rate_enabled && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Second rate name</Label>
                    <Input value={rateDlg.form.helper_rate_label || ""} onChange={(e) => setRateDlg(s => ({ ...s, form: { ...s.form, helper_rate_label: e.target.value } }))} placeholder="Overtime rate" data-testid="rates-dr2-label" />
                  </div>
                  <div>
                    <Label className="text-xs">Default (£)</Label>
                    <Input type="number" step="0.01" value={rateDlg.form.helper_rate ?? 0} onChange={(e) => setRateDlg(s => ({ ...s, form: { ...s.form, helper_rate: parseFloat(e.target.value) || 0 } }))} data-testid="rates-helper-rate" />
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Rates are pre-filled on every new PO for this operative and can be changed per PO.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDlg({ open: false, user: null, saving: false, form: {} })}>Cancel</Button>
            <Button onClick={saveRateProfile} disabled={rateDlg.saving} data-testid="rates-save-btn">
              {rateDlg.saving ? "Saving…" : "Save Rates"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Home location editor */}
      <Dialog open={homeDlg.open} onOpenChange={(o) => { if (!o) setHomeDlg({ open: false, user: null, saving: false, address: "", latitude: null, longitude: null }); }}>
        <DialogContent className="max-w-md" data-testid="in-house-team-home-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Home Location — {homeDlg.user?.name}</DialogTitle>
            <DialogDescription>
              The operative's day route is planned from home → every job → back home. They can also set or edit this themselves in the Route Planner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Home address</Label>
            <AddressAutocomplete
              value={homeDlg.address}
              onChange={(v) => setHomeDlg(s => ({ ...s, address: v, latitude: null, longitude: null }))}
              onSelect={(a) => setHomeDlg(s => ({ ...s, address: a.formatted_address || s.address, latitude: a.latitude, longitude: a.longitude }))}
              placeholder="Start typing address or postcode…"
              data-testid="in-house-home-autocomplete"
            />
            <p className="text-[11px] text-muted-foreground">
              Pick a suggestion for pin-point accuracy — a typed address is located automatically on save.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHomeDlg({ open: false, user: null, saving: false, address: "", latitude: null, longitude: null })}>Cancel</Button>
            <Button onClick={saveHomeLocation} disabled={homeDlg.saving} data-testid="in-house-home-save-btn">
              {homeDlg.saving ? "Saving…" : "Save Home Location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
